// frontend/src/hooks/useWebSocket.ts

import { useEffect, useRef } from 'react';
import { io, Socket } from 'socket.io-client';
import { usePriceStore } from '@/store/usePriceStore';
import type { PriceData, PegStatus } from '@/types';

const SOCKET_URL = import.meta.env.VITE_SOCKET_URL || 'http://localhost:3001';

export function useWebSocket() {
  const socketRef = useRef<Socket | null>(null);
  const { updatePrices } = usePriceStore();

  useEffect(() => {
    // Initialize socket connection
    socketRef.current = io(SOCKET_URL, {
      transports: ['websocket'],
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionAttempts: 5,
    });

    const socket = socketRef.current;

    // Connection events
    socket.on('connect', () => {
      console.log('✅ WebSocket connected');
      
      // Subscribe to price updates
      socket.emit('subscribe:prices');
      socket.emit('subscribe:pegs');
    });

    socket.on('disconnect', () => {
      console.log('❌ WebSocket disconnected');
    });

    socket.on('connect_error', (error) => {
      console.error('WebSocket error:', error);
    });

    // Price updates
    socket.on('prices:initial', (prices: PriceData[]) => {
      console.log('📊 Received initial prices');
      updatePrices(prices);
    });

    socket.on('prices:update', (prices: PriceData[]) => {
      console.log('📊 Price update received');
      updatePrices(prices);
    });

    // Peg updates
    socket.on('pegs:initial', (pegs: PegStatus[]) => {
      console.log('🎯 Received initial peg status');
      // Handle peg status
    });

    socket.on('pegs:update', (pegs: PegStatus[]) => {
      console.log('🎯 Peg update received');
      // Handle peg updates
    });

    socket.on('peg:alert', (alert: any) => {
      console.warn('⚠️ Peg alert:', alert);
      // Handle peg alerts
    });

    // Cleanup on unmount
    return () => {
      socket.disconnect();
    };
  }, [updatePrices]);

  return socketRef.current;
}
